import { z } from 'zod';
import { phoneRegex } from './authSchemas';
import { kycDocumentTypeSchema, kycFileSchema, kycOtherDocumentTagSchema } from './kycSchema';

// v1.18.0 — one row of the optional "Other Documents" repeater on Onboard
// Client. Both fields required once a row exists at all (an admin who adds
// a row and leaves it half-filled should get a clear error, not have the
// row silently dropped).
const onboardOtherDocumentSchema = z.object({
  tag: kycOtherDocumentTagSchema,
  file: kycFileSchema,
});

// POST /admin/investors — first/last name, email, Nigerian-format phone
// (same rule as self-service registration), a required document type +
// 1-2 files (unlike the investor's own KYC submission, this is mandatory
// here), and send_credentials which just toggles what happens server-side
// (email vs. one-time plaintext password) rather than changing validation.
export const onboardInvestorSchema = z
  .object({
    first_name: z.string().min(1, 'Enter a first name'),
    last_name: z.string().min(1, 'Enter a last name'),
    email: z.string().email('Enter a valid email'),
    phone: z.string().regex(phoneRegex, 'Use international format, e.g. +2348012345678'),
    send_credentials: z.boolean(),
    document_type: kycDocumentTypeSchema,
    documents: z.array(kycFileSchema).min(1, 'Upload at least one document').max(2, 'Upload at most 2 documents'),
    // Optional — an admin can onboard a client without attaching any
    // "other" documents up front and add them later from Investor Detail.
    other_documents: z.array(onboardOtherDocumentSchema).default([]),
  })
  // One-per-tag, enforced client-side (the checklist a tag represents can
  // only ever have one current document against it) — a duplicate tag
  // would otherwise silently overwrite/confuse rather than erroring.
  .superRefine((values, ctx) => {
    const seen = new Map();
    values.other_documents.forEach((row, idx) => {
      const key = row.tag.trim().toLowerCase();
      if (seen.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['other_documents', idx, 'tag'],
          message: `Already added a document for "${row.tag}" above.`,
        });
      }
      seen.set(key, true);
    });
  });
